import {useEffect, useRef, useState} from 'react';
import {QueryFunctionContext, useQuery} from 'react-query';
import api from 'api';
import {DataItem, Driver, ListResponse, Vehicle} from 'types';
import ViewDetails from 'components/ViewDetails';
import useSort from 'hooks/useSort';
import useDebounce from 'hooks/useDebounce';
import {EmployeStatus} from '../Employees/useEmployees';
import arrow_bottom from 'pages/DeliveryPlanner/components/Ticket/arrow_bottom.svg';
import {useHistory} from 'react-router-dom';
import {DropResult} from 'react-beautiful-dnd';
import {phoneFormat} from 'helpers/phoneFormat';

export type OnsiteWorkerScreenTabs = 'workers_tab' | 'teams_tab';

async function getTeams({queryKey}: QueryFunctionContext) {
  const [, order, keyWord] = queryKey;
  const {data} = await api.get<ListResponse<Vehicle>>('/teams/', {
    params: {order, search: keyWord ? keyWord : undefined},
  });

  return data;
}

async function getWorkers() {
  const {data} = await api.get<ListResponse<Driver>>('/users/', {
    params: {role: 'driver'},
  });
  return data;
}

function mapTeams(team: Vehicle[]) {
  return team.map((v) => ({
    ...v,
    members: v.drivers
      ? v.drivers
          .map((item) => item.first_name + ' ' + item.last_name)
          .slice(0, 2)
          .join(', ') + (v.drivers.length > 2 ? '...' : '')
      : '',
    view: v.id ? <ViewDetails to={`/people/teams/${v.id}`} /> : null,
  }));
}

export const returnDrivers = (drivers: Driver[]) => {
  return drivers.map((d) => ({
    ...d,
    full_name: d.first_name + ' ' + d.last_name,
    view: <ViewDetails to={`/people/employees/${d.user_id}`} />,
    active: <EmployeStatus id={d.user_id} active={d.is_active === 'True'} />,
    phone: phoneFormat(d.phone),
  }));
};

export const returnWorkers = (drivers: Driver[]) => {
  return drivers.map((w) => ({
    ...w,
    driver_field_role: w?.driver_field_role?.name,
    full_name: w.first_name + ' ' + w.last_name,
    view: <ViewDetails to={`/people/employees/${w.id}`} />,
    team: w.team?.name,
    active: <EmployeStatus id={w.id!} active={w.is_active === 'True'} />,
    phone: phoneFormat(w.phone),
  }));
};

function useTeams() {
  const {push} = useHistory();
  const [keyWord, setKeyWord] = useState(
    sessionStorage.getItem(`search_team`) || ''
  );
  const [selectedRows, setSelectedRows] = useState<number | string>('default');
  const [activeTab, setActiveTab] = useState<OnsiteWorkerScreenTabs>(
    (sessionStorage.getItem('workers_tab') as OnsiteWorkerScreenTabs) ||
      'workers_tab'
  );
  const {sortBy, sortOrder, order, onSort} = useSort('name');

  const searchDebounced = useDebounce(keyWord, 400);

  const {data: teams, isLoading} = useQuery(
    ['teams', order, searchDebounced],
    getTeams
  );
  const {data: workers, isLoading: isWorkersLoading} = useQuery(
    ['workers'],
    getWorkers
  );
  const initializedCount = useRef(0);

  const [data, setData] = useState<Vehicle[]>([]);

  type Team = {
    id: string;
    drivers: number[];
    name: string;
    model: string;
    travel_method: string;
  };

  const updateTeams = async (team1: Team, team2: Team) => {
    try {
      if (team1.id !== 'default') {
        api.put(`/teams/${team1.id}/`, team1);
      }
      if (team2.id !== 'default') {
        api.put(`/teams/${team2.id}/`, team2);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (teams) {
      if (initializedCount.current < 2) {
        setData(
          mapTeams(teams.results).map((v) => ({
            ...v,
            id: v.id || 'default',
            drivers: returnDrivers(v.drivers || []),
            dropdown: <ArrowBottom />,
          }))
        );
      }
    }
    initializedCount.current++;
  }, [teams]);

  const onAddMemberClick = (item: DataItem) => {
    push(`/people/employees/new`, {
      state: {
        team_id: item.id === 'default' ? null : item.id,
        isStaff: false,
      },
    });
  };

  const reorder = (list: Driver[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = (result: DropResult) => {
    const {source, destination, draggableId} = result;

    if (
      !destination ||
      !source ||
      source.droppableId === destination.droppableId
    ) {
      if (destination && source.droppableId === destination.droppableId) {
        const clonedData = [...data];
        const teamId = source.droppableId.split('-')[1];

        const index = clonedData.findIndex(
          (d) => d.id === (teamId === 'default' ? 'default' : Number(teamId))
        );

        clonedData[index].drivers = reorder(
          clonedData[index].drivers || [],
          source.index,
          destination.index
        );

        setData(clonedData);
        return;
      }
      return;
    }

    const clonedData = [...data];

    const toId = destination.droppableId.split('-')[1];
    const fromId = source.droppableId.split('-')[1];

    const toIndex = clonedData.findIndex(
      (d) => d.id === (toId === 'default' ? 'default' : Number(toId))
    );

    const fromIndex = clonedData.findIndex(
      (d) => d.id === (fromId === 'default' ? 'default' : Number(fromId))
    );

    const memberId = draggableId.split('-')[1];

    const member = clonedData[fromIndex].drivers?.find(
      (d) => d.id === Number(memberId)
    );

    clonedData[fromIndex].drivers = clonedData[fromIndex].drivers?.filter(
      (d) => d.id !== Number(memberId)
    );

    clonedData[toIndex].drivers = [
      ...(clonedData[toIndex].drivers || []),
      member as any,
    ];

    updateTeams(
      {
        id: fromId,
        drivers: clonedData[fromIndex].drivers?.map((d) => d.id) || [],
        name: clonedData[fromIndex].name,
        model: clonedData[fromIndex].model,
        travel_method: clonedData[fromIndex].travel_method || '',
      },
      {
        id: toId,
        drivers: clonedData[toIndex].drivers?.map((d) => d.id) || [],
        name: clonedData[toIndex].name,
        model: clonedData[toIndex].model,
        travel_method: clonedData[toIndex].travel_method || '',
      }
    );

    setData(clonedData);
    setSelectedRows(toId === 'default' ? 'default' : Number(toId));
  };

  return {
    data,
    vehicles: data,
    teams: data,
    workers: workers ? returnWorkers(workers.results) : [],
    isLoading,
    isWorkersLoading,
    sortBy,
    sortOrder,
    activeTab,
    onSort,
    keyWord,
    setData,
    setKeyWord,
    selectedRows,
    setSelectedRows,
    updateTeams,
    showSubMembers: selectedRows,
    onAddMemberClick,
    handleDragEnd,
    setActiveTab,
  };
}

const ArrowBottom = () => {
  return (
    <img
      alt="arrow bottom"
      src={arrow_bottom}
      style={{
        width: 30,
        height: 30,
        marginTop: 5,
      }}
    />
  );
};

export default useTeams;
